import { Component, EventEmitter, Input, Output, WritableSignal, signal } from '@angular/core';
import { Subtask } from '../../../shared/interfaces';
import {
  FileInfo,
  FileUploadError,
  GALAXY_UPLOADER_SERVICE_TOKEN,
  GalaxyUploaderModule,
} from '@vendasta/galaxy/uploader';
import { SubtasksUploaderService } from './subtasks-uploader.service';
import { TaskAttachmentService, TaskIdentity, TaskSdkService } from '@vendasta/task';
import { TaskIdentityInterface } from '@vendasta/task-manager';
import { retry, take } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';

@Component({
  selector: 'project-tracker-subtasks-uploader',
  standalone: true,
  imports: [GalaxyUploaderModule, GalaxyLoadingSpinnerModule],
  templateUrl: './subtasks-uploader.component.html',
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: SubtasksUploaderService }],
})
export class SubtasksUploaderComponent {
  @Input() subtask: Subtask;
  @Output() fileUploaded: EventEmitter<FileInfo> = new EventEmitter();
  @Output() fileUploadError: EventEmitter<FileUploadError> = new EventEmitter();

  TEN_MB = 10 * 1024 * 1024;
  loading: WritableSignal<boolean> = signal(false);

  constructor(
    private attachmentService: TaskAttachmentService,
    private snackbarService: SnackbarService,
  ) {}

  handleFileUploaded(fileInfo: FileInfo): void {
    this.loading.set(true);
    const namespace = TaskSdkService.buildAccountGroupNamespace(this.subtask.partnerId, this.subtask.accountGroupId);
    // subtaskID is in format "/<parentTaskId>/<subtaskId>  "
    const subtaskIdParts: string[] = this.subtask.subtaskId.split('/');
    if (subtaskIdParts?.length !== 3) {
      this.fileUploadError.emit({
        fileInfo,
        error: new Error('Invalid subtaskId'),
      });
    }
    const parentPath = '/' + subtaskIdParts[1] + '/';
    const taskId = subtaskIdParts[2];
    const identity: TaskIdentityInterface = {
      namespace,
      parentPath,
      taskId,
    };

    this.attachmentService
      .addAttachment(new TaskIdentity(identity), {
        name: fileInfo.name,
        url: fileInfo.url,
        uploadingPersonaType: 'smb',
      })
      .pipe(take(1), retry({ count: 3, delay: 1000 }))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack('ATTACHMENTS.ADD_SUCCESS');
          this.fileUploaded.emit(fileInfo);
          this.loading.set(false);
        },
        error: (error) => {
          this.fileUploadError.emit(error);
          this.loading.set(false);
        },
      });
  }

  handleFileUploadErrored(error: FileUploadError): void {
    this.fileUploadError.emit(error);
  }
}
